/**
 * Кнопка тулбара markdown-редактора: цвет текста или выделение фоном.
 * Открывает палитру из `@lens/shared` и «Без цвета».
 *
 * onMouseDown с preventDefault на кнопке и на образцах обязателен: иначе
 * клик уводит фокус из ProseMirror, inline-редактор по blur сохраняет и
 * закрывается, а выделение теряется до применения цвета.
 */
import { useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { FontColorsOutlined, HighlightOutlined } from '@ant-design/icons';
import type { Editor } from '@tiptap/react';
import { MARKDOWN_HIGHLIGHT_COLORS, MARKDOWN_TEXT_COLORS } from '@lens/shared';
import './markdown-field.css';

interface Props {
  editor: Editor;
  kind: 'text' | 'highlight';
  /** Стиль самой кнопки — у inline-редактора и модалки тулбары разного размера. */
  buttonStyle?: React.CSSProperties;
}

export function ColorPickerButton({ editor, kind, buttonStyle }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const isText = kind === 'text';
  const markName = isText ? 'textColor' : 'highlight';
  const attrName = isText ? 'color' : 'bg';
  const palette = isText ? MARKDOWN_TEXT_COLORS : MARKDOWN_HIGHLIGHT_COLORS;
  const current = (editor.getAttributes(markName) as Record<string, string | undefined>)[attrName];

  const apply = (key: string | null): void => {
    const chain = editor.chain().focus();
    if (key) chain.setMark(markName, { [attrName]: key }).run();
    else chain.unsetMark(markName).run();
    setOpen(false);
  };

  const swatch = (key: string | null, label: string): JSX.Element => (
    <Tooltip title={label} key={key ?? 'none'} mouseEnterDelay={0.4}>
      <button
        type="button"
        className={`lens-color-swatch${current === key || (!current && key === null) ? ' is-active' : ''}`}
        aria-label={label}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => apply(key)}
      >
        {key === null ? (
          <span className="lens-color-swatch-none" />
        ) : isText ? (
          <span data-color={key} className="lens-color-swatch-letter">А</span>
        ) : (
          <mark data-bg={key} className="lens-color-swatch-fill" />
        )}
      </button>
    </Tooltip>
  );

  const content = (
    <div className="lens-color-palette" onMouseDown={(e) => e.preventDefault()}>
      {swatch(null, isText ? 'Обычный цвет' : 'Без выделения')}
      {palette.map((c) => swatch(c.key, c.label))}
    </div>
  );

  return (
    <Popover content={content} trigger="click" open={open} onOpenChange={setOpen} placement="bottom" arrow={false}>
      {/* Пока палитра открыта, подсказку прячем — иначе она ложится поверх образцов. */}
      <Tooltip title={open ? '' : isText ? 'Цвет текста' : 'Выделение цветом'}>
        <Button
          type="text"
          size="small"
          aria-label={isText ? 'Цвет текста' : 'Выделение цветом'}
          onMouseDown={(e) => e.preventDefault()}
          style={buttonStyle}
          icon={
            <span className="lens-color-btn-icon">
              {isText ? <FontColorsOutlined /> : <HighlightOutlined />}
              <span
                className="lens-color-btn-bar"
                style={{ background: current ? `var(--md-${isText ? 'tc' : 'hl'}-${current})` : 'transparent' }}
              />
            </span>
          }
        />
      </Tooltip>
    </Popover>
  );
}
